/**
 * Payment Instructions API Hook
 *
 * Fetches payment instructions configuration for displaying on invoices.
 * This data rarely changes, so we use a long stale time.
 */

import { useQuery } from "@tanstack/react-query"

import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"

export interface PaymentInstructions {
  zelle_email: string
  office_address: string
  office_hours: string
  check_payable_to: string
}

/**
 * Fetch payment instructions for invoices.
 *
 * Returns Zelle email, office address/hours, and check payable info.
 */
export function usePaymentInstructions() {
  return useQuery({
    queryKey: ["payment-instructions"],
    queryFn: async (): Promise<PaymentInstructions> => {
      const response = await api.instance.get(
        "/api/v1/settings/payment-instructions",
      )
      return unwrapStandardResponse<PaymentInstructions>(
        response.data,
      ) as PaymentInstructions
    },
    staleTime: 1000 * 60 * 60, // 1 hour (rarely changes)
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}
